// Builds the approvals table of a paper from its route (sent by the server) and the
// approvals known locally, which grow when the user approves inside the modal.
import * as requisitionTypes from "../../../../general/paper-state";

const fullName = (person) =>
  [person && person.firstName, person && person.lastName].filter(Boolean).join(" ");

const sameEmail = (a, b) =>
  !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

// A step that fills lines (e.g. the commitment code and sum) instead of approving.
export const isInfoEntry = (entry) => !!entry && (entry.infoLabels || []).length > 0;

const indexApprovals = (approvals) => {
  const byOrder = {};
  // Like the server: the paper is past every order that has a recorded action.
  let reached = null;
  (approvals || []).forEach((approval) => {
    if (byOrder[approval.orderNumber] === undefined) {
      byOrder[approval.orderNumber] = approval;
    }
    reached = reached === null ? approval.orderNumber : Math.max(reached, approval.orderNumber);
  });
  return { byOrder, reached };
};

/**
 * One row per route step the paper has reached or still has to reach, plus recorded
 * approvals whose order no step has any more. Steps the paper skipped (no chief, or
 * behind its position) are left out. Only the current step offers the buttons, to
 * any of its candidates. A pending step shows the viewer when they are one of its
 * candidates, otherwise who it is meant for, with its fallbacks on a separate line.
 */
export const buildApprovalRows = ({ route, approvals, paperState, userEmail, canApprove }) => {
  const recorded = approvals || [];
  const { byOrder, reached } = indexApprovals(recorded);
  const approved = requisitionTypes.isApproved(paperState);
  const waiting = paperState === requisitionTypes.WAITING_APPROVAL;

  const rows = [];
  const used = {};
  let currentAssigned = false;
  (route || []).forEach((entry) => {
    if (entry.type === "RECORDED") {
      return; // rendered from the approvals below
    }
    const approval = byOrder[entry.order];
    if (approval) {
      used[entry.order] = true;
      rows.push(approvalRow(approval, entry.order));
      return;
    }
    if (approved || (reached !== null && entry.order <= reached)) {
      return;
    }
    const current = waiting && !currentAssigned;
    if (current) {
      currentAssigned = true;
    }
    const candidates = entry.candidates || [];
    const me = candidates.find((candidate) => sameEmail(candidate.email, userEmail));
    const fallbacks = candidates.filter((candidate) => candidate.fallback);
    const shown = me ? [me] : candidates.filter((candidate) => !candidate.fallback);
    rows.push({
      key: "step-" + entry.order,
      order: entry.order,
      calling: entry.calling,
      firstName: shown.map((c) => c.firstName).join(" / "),
      lastName: shown.map((c) => c.lastName).join(" / "),
      email: shown.map((c) => c.email).join(" / "),
      displayName: shown.map(fullName).join(" / "),
      fallbackNames: me ? "" : fallbacks.map(fullName).join(", "),
      approved: null,
      date: null,
      infoStep: isInfoEntry(entry),
      canApprove: !!canApprove && current && !!me,
    });
  });
  recorded.forEach((approval) => {
    if (!used[approval.orderNumber]) {
      used[approval.orderNumber] = true;
      rows.push(approvalRow(approval, approval.orderNumber));
    }
  });
  rows.sort((a, b) => a.order - b.order);
  return rows;
};

// Older payloads have no route: rebuild one from the flow's person steps.
export const legacyRoute = (steps) =>
  (steps || [])
    .filter((step) => step.stepType !== "DEPARTMENT_CHIEF" && step.signedBy)
    .map((step) => ({
      order: step.stepOrder,
      type: "USER",
      calling: step.calling,
      canEditPapers: !!step.canEditPapers,
      canSetArticolBugetar: !!step.canSetArticolBugetar,
      candidates: [
        step.signedBy,
        ...(step.fallbacks || []).map((person) => ({ ...person, fallback: true })),
      ],
    }));

/**
 * Edit rights of the step waiting for approval, when the viewer is one of its candidates:
 * a person may sit on several steps, each with its own rights.
 */
export const currentStepRights = ({ route, approvals, paperState, userEmail }) => {
  const none = { canEditPapers: false, canSetArticolBugetar: false, canSetRegistrationNumber: false, infoLabels: [] };
  if (paperState !== requisitionTypes.WAITING_APPROVAL) {
    return none;
  }
  const { byOrder, reached } = indexApprovals(approvals);
  const current = (route || []).find(
    (entry) =>
      entry.type !== "RECORDED" &&
      byOrder[entry.order] === undefined &&
      (reached === null || entry.order > reached)
  );
  if (!current || !(current.candidates || []).some((c) => sameEmail(c.email, userEmail))) {
    return none;
  }
  return {
    canEditPapers: !!current.canEditPapers,
    canSetArticolBugetar: !!current.canSetArticolBugetar,
    canSetRegistrationNumber: !!current.canSetRegistrationNumber,
    // Non-empty at an info step: one value per label instead of a decision.
    infoLabels: current.infoLabels || [],
  };
};

// A recorded row is an info row when lines were filled, whatever the step has become since.
const approvalRow = (approval, order) => ({
  key: "approval-" + (approval.id !== undefined ? approval.id : order),
  order: order,
  calling: approval.calling,
  firstName: approval.firstName,
  lastName: approval.lastName,
  email: approval.email,
  displayName: fullName(approval),
  fallbackNames: "",
  approved: approval.approved,
  date: approval.date,
  infoStep: !!approval.infoLines,
  canApprove: false,
});
